'use client';

import { Box, Card, CardActionArea, CardContent, Grid, Typography } from '@mui/material';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import AssessmentIcon from '@mui/icons-material/Assessment';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import TitleBar from '../layout/TitleBar';

type ReportCardDef = {
  key: string;
  title: string;
  description: string;
  href: string;
  icon: React.ReactNode;
};

const REPORT_CARDS: ReportCardDef[] = [
  {
    key: 'store-counts',
    title: 'Store counts',
    description:
      'What was counted at each location on a selected count day. Download CSV.',
    href: '/app/inventory-auditor/reports/store-counts',
    icon: <FactCheckIcon sx={{ fontSize: 40 }} color="primary" />,
  },
];

function ReportCard({ card, onOpen }: { card: ReportCardDef; onOpen: () => void }) {
  return (
    <Card elevation={2} sx={{ height: '100%' }}>
      <CardActionArea onClick={onOpen} sx={{ height: '100%', display: 'block', textAlign: 'left' }}>
        <CardContent>
          <Box display="flex" gap={2} alignItems="flex-start">
            <Box
              sx={{
                width: 64,
                height: 64,
                flexShrink: 0,
                borderRadius: 1,
                bgcolor: 'action.hover',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {card.icon}
            </Box>
            <Box minWidth={0} flex={1}>
              <Typography variant="h6" component="div" gutterBottom>
                {card.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {card.description}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

const InventoryAuditorReportsPage: React.FC = () => {
  const navigate = useNavigate();

  const breadcrumbs = [
    { label: 'Home', href: '/app/dashboard' },
    { label: 'Reports' },
  ];

  return (
    <Box>
      <TitleBar breadcrumbs={breadcrumbs} title="Reports" />
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <AssessmentIcon color="action" />
          <Typography variant="body2" color="text.secondary">
            Choose a report to run. More reports will be added here over time.
          </Typography>
        </Box>
        <Grid container spacing={2}>
          {REPORT_CARDS.map((card) => (
            <Grid item xs={12} sm={6} md={4} key={card.key}>
              <ReportCard card={card} onOpen={() => navigate(card.href)} />
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
};

export default InventoryAuditorReportsPage;
